import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth } from '@/auth/RequireAuth';
import { LoginPage } from '@/auth/LoginPage';
import { OtpPage } from '@/auth/OtpPage';
import { EmployeeLayout } from './layouts/EmployeeLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { HomeEntry } from './HomeEntry';
import { InstallPage } from '@/features/install/InstallPage';
import { JoinPage } from '@/features/join/JoinPage';
import { MePage } from '@/features/me/MePage';
import { MyDocumentsPage } from '@/features/me/MyDocumentsPage';
import { ContractTemplatesPage } from '@/features/employees/private/ContractTemplatesPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { MySchedulePage } from '@/features/schedule/MySchedulePage';
import { ConstraintsPage } from '@/features/schedule/ConstraintsPage';
import { SwapsPage } from '@/features/schedule/swaps/SwapsPage';
import { LearningPage } from '@/features/learning/LearningPage';
import { CoursePage } from '@/features/learning/CoursePage';
import { QuizPage } from '@/features/learning/quiz/QuizPage';
import { ProductsPage } from '@/features/products/ProductsPage';
import { ProductPage } from '@/features/products/ProductPage';
import { PointsPage } from '@/features/games/PointsPage';
import { RewardsPage } from '@/features/games/RewardsPage';
import { LeaderboardPage } from '@/features/games/LeaderboardPage';
import { ChecklistsPage } from '@/features/ops/ChecklistsPage';
import { TasksPage } from '@/features/ops/TasksPage';
import { DocumentsPage } from '@/features/documents/DocumentsPage';
import { DocumentPage } from '@/features/documents/DocumentPage';
import { RequestsPage } from '@/features/requests/RequestsPage';
import { SurveysPage } from '@/features/surveys/SurveysPage';
import { DisplayPage } from '@/features/ops/display/DisplayPage';
import { MySalesPage } from '@/features/sales/MySalesPage';
import { ChannelsPage } from '@/features/messages/ChannelsPage';
import { ChannelPage } from '@/features/messages/ChannelPage';
import { ChannelsAdminPage } from '@/features/messages/admin/ChannelsAdminPage';
import { ChannelAdminPage } from '@/features/messages/admin/ChannelAdminPage';
import { AckReportPage } from '@/features/messages/admin/AckReportPage';
import { PeriodsPage } from '@/features/schedule/admin/PeriodsPage';
import { WeekBoardPage } from '@/features/schedule/admin/WeekBoardPage';
import { BoardPage } from '@/features/schedule/admin/BoardPage';
import { PrintSchedulePage } from '@/features/schedule/admin/PrintSchedulePage';
import { ClosuresPage } from '@/features/schedule/admin/ClosuresPage';
import { CoursesPage } from '@/features/learning/admin/CoursesPage';
import { CourseEditorPage } from '@/features/learning/admin/CourseEditorPage';
import { AssignmentsPage } from '@/features/learning/admin/AssignmentsPage';
import { ProductsAdminPage } from '@/features/products/admin/ProductsAdminPage';
import { SpotlightsPage } from '@/features/products/admin/SpotlightsPage';
import { CategoriesPage } from '@/features/products/admin/CategoriesPage';
import { PlanogramPage } from '@/features/products/admin/PlanogramPage';
import { ZonesPage } from '@/features/products/admin/ZonesPage';
import { PlanogramView } from '@/features/products/PlanogramView';
import { RewardsAdminPage } from '@/features/games/admin/RewardsAdminPage';
import { CompetitionsPage } from '@/features/games/admin/CompetitionsPage';
import { AwardsPage } from '@/features/games/admin/AwardsPage';
import { GrantPointsPage } from '@/features/games/admin/GrantPointsPage';
import { ChecklistsAdminPage } from '@/features/ops/admin/ChecklistsAdminPage';
import { TasksAdminPage } from '@/features/ops/admin/TasksAdminPage';
import { DocumentsAdminPage } from '@/features/documents/admin/DocumentsAdminPage';
import { RequestsAdminPage } from '@/features/requests/admin/RequestsAdminPage';
import { SurveysAdminPage } from '@/features/surveys/admin/SurveysAdminPage';
import { DisplayAdminPage } from '@/features/ops/display/DisplayAdminPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { SalesAdminPage } from '@/features/sales/admin/SalesAdminPage';
import { TargetsPage } from '@/features/sales/admin/TargetsPage';
import { SettingsIndexPage } from '@/features/settings/SettingsIndexPage';
import { RequirementsPage } from '@/features/settings/RequirementsPage';
import { ListPage } from '@/features/settings/ListPage';
import { RolesPage } from '@/features/settings/RolesPage';
import { PlatformPage } from '@/features/platform/PlatformPage';
import { EmployeesPage } from '@/features/employees/EmployeesPage';
import { ImportPage } from '@/features/employees/import/ImportPage';
import { ApplicationsPage } from '@/features/employees/join/ApplicationsPage';
import { LoadingScreen } from '@/design/components';
import { AdminModuleRoute } from '@/app/AdminModuleRoute';
import { ModuleRoute } from './ModuleRoute';
import { applyInviteRedirect } from './inviteRedirect';

/** כל /admin בחבילה נפרדת (מסמך התכנון, סעיף 11). */
const AdminLayout = lazy(() =>
  import('./layouts/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);

// חייב לרוץ לפני createBrowserRouter, שקורא את הכתובת הנוכחית פעם אחת.
applyInviteRedirect();

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/login/code', element: <OtpPage /> },
      { path: '/install', element: <InstallPage /> },
      { path: '/join/:token', element: <JoinPage /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <EmployeeLayout />,
        children: [
          { path: '/', element: <HomeEntry /> },
          {
            path: '/schedule',
            element: (
              <ModuleRoute module="schedule">
                <MySchedulePage />
              </ModuleRoute>
            ),
          },
          {
            path: '/schedule/constraints/:periodId?',
            element: (
              <ModuleRoute module="schedule">
                <ConstraintsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/schedule/swaps',
            element: (
              <ModuleRoute module="schedule">
                <SwapsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/messages',
            element: (
              <ModuleRoute module="messages">
                <ChannelsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/messages/:channelId',
            element: (
              <ModuleRoute module="messages">
                <ChannelPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/learning',
            element: (
              <ModuleRoute module="learning_courses">
                <LearningPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/learning/quiz/:quizId',
            element: (
              <ModuleRoute module="learning_courses">
                <QuizPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/learning/:courseId',
            element: (
              <ModuleRoute module="learning_courses">
                <CoursePage />
              </ModuleRoute>
            ),
          },
          {
            path: '/products',
            element: (
              <ModuleRoute module="learning_products">
                <ProductsPage />
              </ModuleRoute>
            ),
          },
          {
            // לפני /products/:productId, אחרת "planogram" נקרא
            // כמזהה מוצר
            path: '/products/planogram',
            element: (
              <ModuleRoute module="learning_planogram">
                <PlanogramView />
              </ModuleRoute>
            ),
          },
          {
            path: '/products/:productId',
            element: (
              <ModuleRoute module="learning_products">
                <ProductPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/points',
            element: (
              <ModuleRoute module="games">
                <PointsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/rewards',
            element: (
              <ModuleRoute module="games">
                <RewardsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/leaderboard',
            element: (
              <ModuleRoute module="games">
                <LeaderboardPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/checklists',
            element: (
              <ModuleRoute module="ops_checklists">
                <ChecklistsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/tasks',
            element: (
              <ModuleRoute module="ops_tasks">
                <TasksPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/documents',
            element: (
              <ModuleRoute module="ops_documents">
                <DocumentsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/documents/:documentId',
            element: (
              <ModuleRoute module="ops_documents">
                <DocumentPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/requests',
            element: (
              <ModuleRoute module="ops_requests">
                <RequestsPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/surveys',
            element: (
              <ModuleRoute module="ops_surveys">
                <SurveysPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/display',
            element: (
              <ModuleRoute module="ops_display">
                <DisplayPage />
              </ModuleRoute>
            ),
          },
          {
            path: '/sales',
            element: (
              <ModuleRoute module="sales">
                <MySalesPage />
              </ModuleRoute>
            ),
          },
          { path: '/me', element: <MePage /> },
          // המסמכים האישיים הם של העובד, ולכן הם באזור האישי ולא
          // במסך נפרד בתפריט (מיגרציה 092)
          {
            path: '/me/documents',
            element: (
              <ModuleRoute module="hr_docs">
                <MyDocumentsPage />
              </ModuleRoute>
            ),
          },
          { path: '/notifications', element: <NotificationsPage /> },
        ],
      },
      {
        path: '/admin/schedule/:periodId/print',
        element: <PrintSchedulePage />,
      },
      {
        path: '/admin',
        element: (
          <Suspense fallback={<LoadingScreen />}>
            <AdminLayout />
          </Suspense>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'employees', element: <EmployeesPage /> },
          { path: 'employees/import', element: <ImportPage /> },
          { path: 'employees/applications', element: <ApplicationsPage /> },
          { path: 'settings', element: <SettingsIndexPage /> },
          { path: 'settings/requirements', element: <RequirementsPage /> },
          { path: 'settings/:list', element: <ListPage /> },
          { path: 'roles', element: <RolesPage /> },
          { path: 'platform', element: <PlatformPage /> },
          {
            path: 'channels',
            element: (
              <AdminModuleRoute module="messages">
                <ChannelsAdminPage />
              </AdminModuleRoute>
            ),
          },
          // כתיבה בערוץ מתוך הקונסולה, בלי לעבור לאפליקציה
          {
            path: 'channels/:channelId',
            element: (
              <AdminModuleRoute module="messages">
                <ChannelAdminPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'messages/reports/:messageId',
            element: (
              <AdminModuleRoute module="messages">
                <AckReportPage />
              </AdminModuleRoute>
            ),
          },
          // הלוח השבועי הוא המסך הראשי. רשימת התקופות נשארת
          // למעקב ולמחיקה, אבל היא לא מה שפותחים כדי לשבץ.
          {
            path: 'schedule',
            element: (
              <AdminModuleRoute module="schedule">
                <WeekBoardPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'schedule/periods',
            element: (
              <AdminModuleRoute module="schedule">
                <PeriodsPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'schedule/closures',
            element: (
              <AdminModuleRoute module="schedule">
                <ClosuresPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'schedule/:periodId',
            element: (
              <AdminModuleRoute module="schedule">
                <BoardPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'learning',
            element: (
              <AdminModuleRoute module="learning_courses">
                <CoursesPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'learning/assignments',
            element: (
              <AdminModuleRoute module="learning_courses">
                <AssignmentsPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'learning/:courseId',
            element: (
              <AdminModuleRoute module="learning_courses">
                <CourseEditorPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'products',
            element: (
              <AdminModuleRoute module="learning_products">
                <ProductsAdminPage />
              </AdminModuleRoute>
            ),
          },
          // מחלקות ו"הכר את המוצר" יושבים בטאב מוצרים ולא כפריטי
          // תפריט משלהם: שניהם על אותו קטלוג, והכניסה אליהם היא
          // כפתור בסרגל של מסך המוצרים.
          {
            path: 'products/categories',
            element: (
              <AdminModuleRoute module="learning_products">
                <CategoriesPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'products/spotlights',
            element: (
              <AdminModuleRoute module="learning_products">
                <SpotlightsPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'products/planogram',
            element: (
              <AdminModuleRoute module="learning_planogram">
                <PlanogramPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'products/planogram/zones',
            element: (
              <AdminModuleRoute module="learning_planogram">
                <ZonesPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'games/points',
            element: (
              <AdminModuleRoute module="games">
                <GrantPointsPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'games/rewards',
            element: (
              <AdminModuleRoute module="games">
                <RewardsAdminPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'games/competitions',
            element: (
              <AdminModuleRoute module="games">
                <CompetitionsPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'games/awards',
            element: (
              <AdminModuleRoute module="games">
                <AwardsPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'ops/checklists',
            element: (
              <AdminModuleRoute module="ops_checklists">
                <ChecklistsAdminPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'ops/tasks',
            element: (
              <AdminModuleRoute module="ops_tasks">
                <TasksAdminPage />
              </AdminModuleRoute>
            ),
          },
          // תבניות המשימה יושבות בטאב משימות ולא בהגדרות: שם
          // מחפשים אותן. אותו מסך גנרי, נתיב אחר.
          {
            path: 'ops/tasks/templates',
            element: (
              <AdminModuleRoute module="ops_tasks">
                <ListPage listKey="task-templates" backTo="/admin/ops/tasks" />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'documents',
            element: (
              <AdminModuleRoute module="ops_documents">
                <DocumentsAdminPage />
              </AdminModuleRoute>
            ),
          },
          // קטגוריות הנהלים יושבות בטאב נהלים ולא בהגדרות: מי
          // שמגלה שחסרה לו קטגוריה מגלה את זה כשהוא כותב נוהל.
          // אותו מסך גנרי, נתיב אחר.
          {
            path: 'documents/categories',
            element: (
              <AdminModuleRoute module="ops_documents">
                <ListPage listKey="doc-categories" backTo="/admin/documents" />
              </AdminModuleRoute>
            ),
          },
          // תבניות החוזים יושבות תחת עובדים מאותה סיבה: מי
          // שמוציא חוזה הוא מי שמגלה שהתבנית צריכה שינוי
          {
            path: 'employees/contract-templates',
            element: (
              <AdminModuleRoute module="hr_docs">
                <ContractTemplatesPage />
              </AdminModuleRoute>
            ),
          },
          {
            // בקשות הן של עובדים, ולכן הן יושבות תחת עובדים ולא
            // כפריט תפריט משלהן
            path: 'employees/requests',
            element: (
              <AdminModuleRoute module="ops_requests">
                <RequestsAdminPage />
              </AdminModuleRoute>
            ),
          },
          // התראות שכבר נשלחו מצביעות על הנתיב הישן, ולכן הוא
          // נשאר כהפניה ולא נמחק
          { path: 'requests', element: <Navigate to="/admin/employees/requests" replace /> },
          {
            path: 'surveys',
            element: (
              <AdminModuleRoute module="ops_surveys">
                <SurveysAdminPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'ops/display',
            element: (
              <AdminModuleRoute module="ops_display">
                <DisplayAdminPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'sales',
            element: (
              <AdminModuleRoute module="sales">
                <SalesAdminPage />
              </AdminModuleRoute>
            ),
          },
          {
            path: 'sales/targets',
            element: (
              <AdminModuleRoute module="sales">
                <TargetsPage />
              </AdminModuleRoute>
            ),
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
